import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Copy, Search, AlertTriangle, CheckCircle, Info, ExternalLink, History } from 'lucide-react';
import { api } from '../../context/AuthContext';

const RISK_CONFIG = {
  low:      { label: 'Low Risk',      color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30', bar: 'bg-emerald-500', icon: CheckCircle },
  moderate: { label: 'Moderate Risk', color: 'text-amber-600 dark:text-amber-400',    bg: 'bg-amber-50 dark:bg-amber-950/30',    bar: 'bg-amber-500',   icon: Info },
  high:     { label: 'High Risk',     color: 'text-red-600 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-950/30',         bar: 'bg-red-500',     icon: AlertTriangle },
};

export default function DuplicateContentPage() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('check');

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/duplicate-content/history');
      setHistory(data.history || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await api.post('/duplicate-content/check', { url: url.trim() });
      if (data.success) {
        setResult(data.result);
        setActiveTab('result');
        await loadHistory();
      } else {
        setError(data.error || 'Check failed.');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const showResult = (r) => {
    setResult(r);
    setActiveTab('result');
  };

  const riskLevel = result?.duplicate_risk || 'low';
  const riskCfg = RISK_CONFIG[riskLevel] || RISK_CONFIG.low;
  const RiskIcon = riskCfg.icon;

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
          <Copy className="w-5 h-5 text-rose-600 dark:text-rose-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Duplicate Content Check</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Detect if your page content appears verbatim on other domains or has internal canonicalisation issues.
          </p>
        </div>
      </div>

      {/* URL input always visible */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        <form onSubmit={handleCheck} className="flex gap-3 max-w-2xl">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yoursite.com/page-to-check"
            className="flex-1 p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-rose-600 text-white px-5 py-2 rounded-md text-sm hover:bg-rose-700 disabled:opacity-50 shrink-0"
          >
            <Search className="w-4 h-4" />
            {loading ? 'Analysing...' : 'Run Check'}
          </button>
        </form>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'result', label: 'Results', disabled: !result },
          { id: 'history', label: `History (${history.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-rose-500 text-rose-600 dark:text-rose-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 disabled:opacity-40'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Result */}
      {activeTab === 'result' && result && (
        <div className="space-y-4">
          {/* Risk header */}
          <div className={`rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm ${riskCfg.bg}`}>
            <div className="flex items-center gap-4">
              <RiskIcon className={`w-8 h-8 ${riskCfg.color}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className={`text-xl font-bold ${riskCfg.color}`}>{riskCfg.label}</p>
                  <p className={`text-2xl font-black ${riskCfg.color}`}>{result.risk_score}/100</p>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-800">
                  <div className={`h-full rounded-full ${riskCfg.bar}`} style={{ width: `${result.risk_score}%` }} />
                </div>
                <p className="text-sm mt-2 text-slate-700 dark:text-slate-300">{result.summary}</p>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Word Count', value: result.word_count?.toLocaleString() || '—' },
              { label: 'External Copies', value: result.external_copies?.length ?? 0 },
              { label: 'Canonical', value: result.canonical ? 'Set' : 'Missing', color: result.canonical ? 'text-emerald-600' : 'text-red-600' },
              { label: 'Search Hits', value: result.search_hits_count ?? 0 },
            ].map((m) => (
              <div key={m.label} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3 text-center shadow-sm">
                <p className={`text-lg font-bold ${m.color || 'text-slate-900 dark:text-white'}`}>{m.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.label}</p>
              </div>
            ))}
          </div>

          {/* External copies */}
          {result.external_copies?.length > 0 && (
            <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-950 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> External Copies Detected
              </h3>
              <div className="space-y-2">
                {result.external_copies.map((copy, i) => (
                  <div key={i} className="p-2 rounded-lg bg-red-50 dark:bg-red-950/20">
                    <a href={copy.url} target="_blank" rel="noopener noreferrer" className="text-sm text-red-700 dark:text-red-400 hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      {copy.url}
                    </a>
                    {copy.similarity_hint && <p className="text-xs text-slate-500 mt-0.5">{copy.similarity_hint}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issues & recommendations */}
          {(result.canonical_issues?.length > 0 || result.internal_issues?.length > 0) && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-white dark:bg-slate-950 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">Issues Found</h3>
              <ul className="space-y-1">
                {[...(result.canonical_issues || []), ...(result.internal_issues || [])].map((issue, i) => (
                  <li key={i} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>{issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.recommendations?.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Recommendations</h3>
              <ul className="space-y-1.5">
                {result.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />{rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <div className="space-y-2">
          {history.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-12 text-sm">No checks yet.</p>
          ) : (
            history.map((h, i) => {
              const cfg = RISK_CONFIG[h.duplicate_risk] || RISK_CONFIG.low;
              return (
                <div
                  key={h.id || i}
                  onClick={() => showResult(h)}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm flex items-center justify-between gap-4 cursor-pointer hover:border-rose-300 dark:hover:border-rose-700 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{h.url}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{h.title}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</p>
                    <p className="text-xs text-slate-400">{h.checked_at ? new Date(h.checked_at).toLocaleDateString() : ''}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </motion.div>
  );
}
